import { useSearchParams } from "react-router-dom";
import Jupiter from "../../components/Jupiter/Jupiter";
import Header from "../../components/Jupiter/Header/Header";
import { useEffect, useState } from "react";
import { Spinner } from "@material-tailwind/react";
import Toast from "../../components/Toast/Toast";
import { API_URL } from "../../core/constants";
import axios from "axios";
import { getJupiterToken } from "../../core/cookie";

const JupiterViewDocument = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [notAuthorised, setNotAuthorised] = useState(false);
  const [searchParams] = useSearchParams();

  const id = searchParams.get("id");
  const hash = searchParams.get("hash");
  const type = searchParams.get("type");
  const mime = searchParams.get("mime");

  useEffect(() => {
    const getDocument = async () => {
      setLoading(true);
      axios
        .get(`${API_URL}/aws/s3/${id}/${hash}/${type}`, {
          responseType: "blob",
          headers: {
            "x-jupiter-token": getJupiterToken(),
          },
          withCredentials: true,
          validateStatus: () => true, // Treat all status codes as valid
        })
        .then((response) => {
          if (response.status === 404) {
            setNotFound(true);
          }

          if (response.status === 401) {
            setNotAuthorised(true);
          }

          if (response.status === 500 || response.status === 403) {
            setError(true);
            Toast.popError("Something has gone wrong. Please try again.");
          }

          if (response.status === 200) {
            const blob = new Blob([response.data], { type: mime });
            const url = URL.createObjectURL(blob);

            setNotAuthorised(false);
            setNotFound(false);
            setDocumentUrl(url);
          }
        })
        .catch((error) => {
          console.error("Error during document request:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    getDocument();

    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
    // eslint-disable-next-line
  }, [id, hash, type, mime]);

  const renderLoading = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <Spinner className="h-12 w-12" />
          <span>One moment...</span>
        </div>
      </div>
    );
  };

  const renderError = () => {
    return (
      <div className="w-full flex">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <div className="w-16 h-16 mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
              />
            </svg>
          </div>
          <span>An error has occurred, please try again.</span>
        </div>
      </div>
    );
  };

  const renderNotFound = () => {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6">
        <div className="w-16 h-16 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M323.8 477.2c-38.2 10.9-78.1-11.2-89-49.4l-5.7-20c-3.7-13-10.4-25-19.5-35l-51.3-56.4c-8.9-9.8-8.2-25 1.6-33.9s25-8.2 33.9 1.6l51.3 56.4c14.1 15.5 24.4 34 30.1 54.1l5.7 20c3.6 12.7 16.9 20.1 29.7 16.5s20.1-16.9 16.5-29.7l-5.7-20c-5.7-19.9-14.7-38.7-26.6-55.5c-5.2-7.3-5.8-16.9-1.7-24.9s12.3-13 21.3-13L448 288c8.8 0 16-7.2 16-16c0-6.8-4.3-12.7-10.4-15c-7.4-2.8-13-9-14.9-16.7s.1-15.8 5.3-21.7c2.5-2.8 4-6.5 4-10.6c0-7.8-5.6-14.3-13-15.7c-8.2-1.6-15.1-7.3-18-15.2s-1.6-16.7 3.6-23.3c2.1-2.7 3.4-6.1 3.4-9.9c0-6.7-4.2-12.6-10.2-14.9c-11.5-4.5-17.7-16.9-14.4-28.8c.4-1.3 .6-2.8 .6-4.3c0-8.8-7.2-16-16-16l-97.5 0c-12.6 0-25 3.7-35.5 10.7l-61.7 41.1c-11 7.4-25.9 4.4-33.3-6.7s-4.4-25.9 6.7-33.3l61.7-41.1c18.4-12.3 40-18.8 62.1-18.8L384 32c34.7 0 62.9 27.6 64 62c14.6 11.7 24 29.7 24 50c0 4.5-.5 8.8-1.3 13c15.4 11.7 25.3 30.2 25.3 51c0 6.5-1 12.8-2.8 18.7C504.8 238.3 512 254.3 512 272c0 35.3-28.6 64-64 64l-92.3 0c4.7 10.4 8.7 21.2 11.8 32.2l5.7 20c10.9 38.2-11.2 78.1-49.4 89zM32 384c-17.7 0-32-14.3-32-32L0 128c0-17.7 14.3-32 32-32l64 0c17.7 0 32 14.3 32 32l0 224c0 17.7-14.3 32-32 32l-64 0z" />
          </svg>
        </div>
        <div className="m-auto justify-center items-center flex flex-col gap-6 text-base">
          <span>This document cannot be found. It may have been removed.</span>
        </div>
      </div>
    );
  };

  const renderNotAuthorised = () => {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6">
        <div className="w-16 h-16 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M256 0c-25.3 0-47.2 14.7-57.6 36c-7-2.6-14.5-4-22.4-4c-35.3 0-64 28.7-64 64l0 165.5-2.7-2.7c-25-25-65.5-25-90.5 0s-25 65.5 0 90.5L106.5 437c48 48 113.1 75 181 75l8.5 0 8 0c1.5 0 3-.1 4.5-.4c91.7-6.2 165-79.4 171.1-171.1c.3-1.5 .4-3 .4-4.5l0-176c0-35.3-28.7-64-64-64c-5.5 0-10.9 .7-16 2l0-2c0-35.3-28.7-64-64-64c-7.9 0-15.4 1.4-22.4 4C303.2 14.7 281.3 0 256 0zM240 96.1l0-.1 0-32c0-8.8 7.2-16 16-16s16 7.2 16 16l0 31.9 0 .1 0 136c0 13.3 10.7 24 24 24s24-10.7 24-24l0-136c0 0 0 0 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 55.9c0 0 0 .1 0 .1l0 80c0 13.3 10.7 24 24 24s24-10.7 24-24l0-71.9c0 0 0-.1 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 172.9c-.1 .6-.1 1.3-.2 1.9c-3.4 69.7-59.3 125.6-129 129c-.6 0-1.3 .1-1.9 .2l-4.9 0-8.5 0c-55.2 0-108.1-21.9-147.1-60.9L52.7 315.3c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L119 336.4c6.9 6.9 17.2 8.9 26.2 5.2s14.8-12.5 14.8-22.2L160 96c0-8.8 7.2-16 16-16c8.8 0 16 7.1 16 15.9L192 232c0 13.3 10.7 24 24 24s24-10.7 24-24l0-135.9z" />
          </svg>
        </div>
        <div className="m-auto justify-center items-center flex flex-col gap-6 text-base">
          <span>You cannot view this document. You are not authorised.</span>
        </div>
      </div>
    );
  };

  const renderDocument = () => {
    // Determine the file type based on the `type` parameter
    if (
      mime === "image/jpeg" ||
      mime === "image/jpg" ||
      mime === "image/png" ||
      mime === "image/gif" ||
      mime === "image/webp"
    ) {
      // Render images in an iframe
      return (
        <div className="w-full flex justify-center border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-100">
          <img src={documentUrl} className="w-full" alt="Document render" />
        </div>
      );
    } else {
      // Provide a download link for other file types
      return (
        <div className="w-full flex justify-center">
          <a
            href={documentUrl}
            download={`document.${mime.split("/")[1]}`}
            className="text-blue-500 underline"
          >
            Download Document
          </a>
        </div>
      );
    }
  };

  if (loading) {
    return renderLoading();
  }

  return (
    <Jupiter>
      <div className="container mx-auto px-4 py-8">
        <Header />
        <>
          <section>
            <div className="flex w-full mb-16">
              <a
                href={`/jupiter/applications/${id}`}
                className="text-black text-base hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
              >
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                    <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
                  </svg>
                </div>
                Back to application
              </a>
            </div>
          </section>
          {error && renderError()}
          {notFound && renderNotFound()}
          {notAuthorised && renderNotAuthorised()}
          {documentUrl && renderDocument()}
          <section></section>
        </>
      </div>
    </Jupiter>
  );
};

export default JupiterViewDocument;
